import { Link } from "wouter";
import TenderAnalyticsSummary from "@/components/dashboard/TenderAnalyticsSummary";
import ViewByCompany from "@/components/dashboard/ViewByCompany";
import ActivityCard from "@/components/dashboard/ActivityCard";
import PackageAnalyticsChart from "@/components/dashboard/PackageAnalyticsChart";
import { Home } from "lucide-react";
import { useSidebarLayout } from "@/hooks/useSidebarLayout";
import { useEffect } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useAppStore } from "@/lib/store";
import { apiRequest } from "@/lib/queryClient";
import { useAdminProject } from "@/hooks/useAdminProject";

export default function Dashboard() {
  const queryClient = useQueryClient();
  const { projectId, selectedProject } = useAdminProject();
  const { getPageHeaderClass } = useSidebarLayout();

  // Get activities data
  const { data: activitiesData, isLoading: activitiesLoading, refetch: refetchActivities } = useQuery({
    queryKey: ["activities-data", selectedProject?.id],
    queryFn: () => apiRequest(`/api/activities-data?type=activities&projectId=${selectedProject?.id}`),
    enabled: !!selectedProject?.id,
    staleTime: 5000,
    refetchInterval: 10000,
  });

  useEffect(() => {
    if (selectedProject?.id && selectedProject?.hubId) {
      queryClient.prefetchQuery({
        queryKey: ["folder-structure", selectedProject.hubId, selectedProject.id],
        queryFn: () => apiRequest(`/api/autodesk/hubs/${selectedProject.hubId}/projects/${selectedProject.id}/folders`)
      });
    }
  }, [selectedProject, queryClient]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Fixed Page Header - single line */}
      <div className={getPageHeaderClass()}>
        <div className="h-full flex items-center justify-between px-6">
          <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
        </div>
      </div>
      
      {/* Main Content with top padding to account for both headers (64px global + 64px page = 128px = pt-32) */}
      <main className="pt-32 pb-8 px-4 sm:px-6 lg:px-8 max-w-full mx-auto">

      {/* Summary Cards with reduced spacing */}
      <div className="mt-6 mb-4">
        <TenderAnalyticsSummary />
      </div>

      {/* Analytics and Activity Cards with minimal spacing */}
      <div className="mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <ActivityCard 
              activities={activitiesData || []} 
              isLoading={activitiesLoading} 
              onRefresh={refetchActivities} 
            />
          </div>
          <div>
            <PackageAnalyticsChart />
          </div>
        </div>
      </div>
      </main>
    </div>
  );
}
